import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Send,
  Star,
  Sparkles,
  Palette,
  Brain,
  Zap,
  Headphones,
  CreditCard,
  MessageSquare,
  User,
  Mail,
  Building,
  Briefcase,
  ChevronDown,
  ChevronUp,
  HelpCircle
} from 'lucide-react';
import { submitGeneralFeedback } from '../apis/supportService';
import { useAuth } from '../context/AuthContext';

const initialFormState = {
  name: '',
  email: '',
  role: '',
  company: '',
  category: 'OTHER',
  message: '',
};

const categories = [
  { value: 'UI', label: 'Usability & Design', icon: Palette, color: 'from-pink-500/20 to-purple-500/20 border-pink-500/30 text-pink-300' },
  { value: 'FEATURE', label: 'AI Note Accuracy', icon: Brain, color: 'from-blue-500/20 to-indigo-500/20 border-blue-500/30 text-blue-300' },
  { value: 'PERFORMANCE', label: 'Speed & Performance', icon: Zap, color: 'from-amber-500/20 to-orange-500/20 border-amber-500/30 text-amber-300' },
  { value: 'SUPPORT', label: 'Customer Support', icon: Headphones, color: 'from-emerald-500/20 to-teal-500/20 border-emerald-500/30 text-emerald-300' },
  { value: 'BILLING', label: 'Billing & Subscriptions', icon: CreditCard, color: 'from-cyan-500/20 to-blue-500/20 border-cyan-500/30 text-cyan-300' },
  { value: 'OTHER', label: 'General / Other', icon: MessageSquare, color: 'from-purple-500/20 to-violet-500/20 border-purple-500/30 text-purple-300' },
];

const ratingDescriptions = {
  1: { emoji: '😢', text: 'Very Dissatisfied', color: 'text-red-400 font-semibold' },
  2: { emoji: '🙁', text: 'Dissatisfied', color: 'text-orange-400 font-semibold' },
  3: { emoji: '😐', text: 'Neutral', color: 'text-yellow-400 font-semibold' },
  4: { emoji: '🙂', text: 'Satisfied', color: 'text-emerald-400 font-semibold' },
  5: { emoji: '😄', text: 'Very Satisfied', color: 'text-green-400 font-semibold' },
};

const RatingsPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialFormState);
  const [rating, setRating] = useState(null);
  const [hoverRating, setHoverRating] = useState(0);
  const [clickedStar, setClickedStar] = useState(null);
  const [showContactDetails, setShowContactDetails] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currentUser, refreshMe } = useAuth();
  const isLoggedIn = !!currentUser;

  // Redirect if they have already submitted a rating
  useEffect(() => {
    if (currentUser?.hasSubmittedFeedback) {
      toast.error('You have already submitted a rating.');
      navigate('/scribe');
    }
  }, [currentUser, navigate]);

  // Prefill details for logged-in users
  useEffect(() => {
    if (!currentUser) return;
    const firstName = currentUser.profile?.firstName || '';
    const lastName = currentUser.profile?.lastName || '';
    const email = currentUser.email || '';
    const company = currentUser.profile?.organization || currentUser.profile?.practiceName || '';
    const role = currentUser.profile?.roleTitle || '';
    const name = (firstName || lastName) ? `${firstName} ${lastName}`.trim() : (currentUser.email?.split('@')[0] || '');
    setFormData((prev) => ({
      ...prev,
      name: prev.name || name,
      email: prev.email || email,
      company: prev.company || company,
      role: prev.role || role,
    }));
  }, [currentUser]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStarClick = (star) => {
    setRating(star);
    setClickedStar(star);
    // Remove the pop animation class after it plays (300ms)
    setTimeout(() => {
      setClickedStar(null);
    }, 300);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const hasRating = rating !== null && rating > 0;
    const hasMessage = formData.message && formData.message.trim() !== '';

    if (!hasRating && !hasMessage) {
      toast.error('Please select a star rating or enter review comments before submitting.');
      return;
    }

    setIsSubmitting(true);

    try {
      await submitGeneralFeedback({
        rating: rating || null,
        category: formData.category,
        message: formData.message || null,
        submittedName: formData.name || null,
        submittedEmail: formData.email || null,
        role: formData.role || null,
        company: formData.company || null,
      });

      toast.success('Thank you for rating us! Your review has been submitted.');
      if (refreshMe) {
        await refreshMe();
      }
      navigate('/scribe');
    } catch (error) {
      const message = error?.response?.data?.message || (typeof error?.response?.data?.error === 'string' ? error.response.data.error : null) || 'We could not submit your rating right now.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900 text-white">
      {/* Component-scoped custom animations */}
      <style>{`
        @keyframes star-pop {
          0% { transform: scale(1); }
          50% { transform: scale(1.35); }
          100% { transform: scale(1); }
        }
        .animate-star-pop {
          animation: star-pop 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        @keyframes emoji-slide {
          0% { opacity: 0; transform: translateY(6px) scale(0.9); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-emoji-slide {
          animation: emoji-slide 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
      `}</style>

      {/* Decorative Background Glows */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(192,132,252,0.18),_transparent_55%)]" />
      <div className="pointer-events-none absolute -left-20 -top-20 h-96 w-96 rounded-full bg-purple-600/10 blur-[120px] animate-pulse duration-10000" />
      <div className="pointer-events-none absolute -right-20 bottom-10 h-96 w-96 rounded-full bg-indigo-600/10 blur-[130px] animate-pulse duration-7000" />

      {/* Floating back button (top-left) */}
      <button
        type="button"
        onClick={() => {
          const activeSessionId = typeof window !== 'undefined' ? localStorage.getItem("activeSessionId") : null;
          if (activeSessionId) {
            navigate(`/scribe?sessionId=${activeSessionId}`);
          } else {
            navigate('/scribe');
          }
        }}
        className="fixed left-4 top-5 z-30 inline-flex items-center gap-2 rounded-full border border-white/10 bg-slate-900/55 px-4 py-2 text-xs font-semibold text-white shadow-lg backdrop-blur-md hover:bg-white/10 hover:border-white/20 transition-all duration-300 sm:left-8 sm:top-6"
        aria-label="Go back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>

      <div className="relative mx-auto flex min-h-screen w-full max-w-5xl items-center px-4 pb-12 pt-20 sm:px-6 lg:px-8">
        <div className="grid w-full gap-8 rounded-3xl border border-white/10 bg-slate-900/40 p-6 shadow-[0_32px_128px_-32px_rgba(147,51,234,0.35)] backdrop-blur-xl lg:grid-cols-[0.9fr_1.1fr] lg:gap-12 lg:p-10">

          {/* Left Column Information */}
          <div className="flex flex-col justify-between py-2">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-purple-500/20 bg-purple-500/10 px-4 py-1.5 text-xs font-medium uppercase tracking-wider text-purple-300">
                <Sparkles className="h-3.5 w-3.5 text-purple-400" /> Share your experience
              </div>
              <div className="space-y-4">
                <h1 className="text-3xl font-bold leading-tight tracking-tight sm:text-4xl bg-gradient-to-r from-white via-purple-100 to-purple-300 bg-clip-text text-transparent">
                  Rate your experience with VirtualScribe
                </h1>
                <p className="text-sm leading-relaxed text-purple-200/70 sm:text-base">
                  Your feedback shapes the future of VirtualScribe. Tell us what's working well, what can be improved, or share any suggestions you have for our clinical assistant.
                </p>
              </div>
            </div>

            {/* Support Ticket Section */}
            <div className="mt-8 rounded-2xl border border-white/5 bg-white/5 p-5 backdrop-blur-sm">
              <div className="flex gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-purple-500/10 text-purple-300 border border-purple-500/20">
                  <HelpCircle className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-white">Need technical help?</h4>
                  <p className="mt-1 text-xs text-purple-200/60 leading-relaxed">
                    If you are facing a bug or account issue, please create a ticket so our engineering team can help you immediately.
                  </p>
                  <a
                    href="/support-ticket"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    Submit a support ticket &rarr;
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column Form Container */}
          <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-6 text-white shadow-xl shadow-purple-950/20">
            <form onSubmit={handleSubmit} className="grid gap-6">

              {/* Star Rating Selection */}
              <div className="grid gap-2">
                <label className="font-semibold text-purple-200 text-sm tracking-wide">Overall Rating</label>
                <div className="flex flex-wrap items-center gap-4 rounded-xl border border-white/5 bg-white/5 p-3.5">
                  <div className="flex items-center gap-1.5 py-1">
                    {[1, 2, 3, 4, 5].map((star) => {
                      const isActive = hoverRating ? star <= hoverRating : rating !== null && star <= rating;
                      const isPop = clickedStar === star;
                      return (
                        <button
                          key={star}
                          type="button"
                          onClick={() => handleStarClick(star)}
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          disabled={isSubmitting}
                          className="focus:outline-none relative transition-transform duration-100 active:scale-90"
                          style={{ tapHighlightColor: 'transparent' }}
                        >
                          <Star
                            className={`h-10 w-10 cursor-pointer transition-all duration-300 ease-out transform hover:scale-125 hover:-rotate-12 ${isPop ? 'animate-star-pop' : ''
                              } ${isActive
                                ? 'fill-amber-400 text-amber-400 drop-shadow-[0_0_15px_rgba(251,191,36,0.85)] scale-105'
                                : 'text-slate-600 hover:text-amber-300/80'
                              }`}
                          />
                        </button>
                      );
                    })}
                  </div>

                  {/* Dynamic Emotion Display */}
                  <span
                    key={hoverRating || rating || 'none'}
                    className="flex items-center gap-2 min-w-[160px] animate-emoji-slide"
                  >
                    {(hoverRating || rating) ? (
                      <>
                        <span className="text-3xl filter drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]">
                          {ratingDescriptions[hoverRating || rating]?.emoji}
                        </span>
                        <div className="flex flex-col">
                          <span className="text-xs text-slate-400 font-bold uppercase tracking-wider">Score: {hoverRating || rating}/5</span>
                          <span className={`text-sm ${ratingDescriptions[hoverRating || rating]?.color}`}>
                            {ratingDescriptions[hoverRating || rating]?.text}
                          </span>
                        </div>
                      </>
                    ) : (
                      <span className="text-slate-500 font-normal text-xs italic tracking-wide">Select a score</span>
                    )}
                  </span>
                </div>
              </div>

              {/* Category selector grid */}
              <div className="grid gap-2 text-sm">
                <label className="font-semibold text-purple-200 tracking-wide">
                  Feedback Topic
                </label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {categories.map((cat) => {
                    const isSelected = formData.category === cat.value;
                    const IconComponent = cat.icon;
                    return (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, category: cat.value }))}
                        disabled={isSubmitting}
                        className={`group flex flex-row items-center gap-2.5 rounded-xl border px-3 py-1.5 text-left transition-all duration-300 focus:outline-none ${isSelected
                          ? `bg-gradient-to-r ${cat.color} scale-[1.02] shadow-[0_4px_12px_-2px_rgba(147,51,234,0.25)] border-purple-500/50 text-white`
                          : 'border-white/5 bg-white/5 text-slate-300 hover:border-white/15 hover:bg-white/10 hover:text-white'
                          }`}
                      >
                        <div className={`rounded-lg p-1.5 transition-transform duration-300 group-hover:scale-110 shrink-0 ${isSelected ? 'bg-white/15 text-white' : 'bg-slate-950/30 text-purple-300/80 group-hover:text-purple-300'
                          }`}>
                          <IconComponent className="h-4 w-4" />
                        </div>
                        <span className="text-xs font-semibold tracking-wide leading-tight">{cat.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Review Message Text Area */}
              <div className="grid gap-2 text-sm">
                <label htmlFor="message" className="font-semibold text-purple-200 tracking-wide">
                  Review & Comments
                </label>
                <div className="relative">
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us what you liked, what frustrated you, or what could be improved."
                    rows={5}
                    className="w-full resize-none rounded-xl border border-white/10 bg-slate-950/40 p-4 text-sm text-white placeholder-slate-500 focus:border-purple-500/60 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all duration-300"
                    disabled={isSubmitting}
                  />
                  <div className="absolute bottom-3 right-3 text-xs font-semibold text-slate-500 tracking-wider">
                    {formData.message?.length || 0} characters
                  </div>
                </div>
              </div>

              {/* Collapsible Contact Section (only for non-logged-in users) */}
              {!isLoggedIn && (
                <div className="rounded-xl border border-white/5 bg-white/5 overflow-hidden transition-all duration-300">
                  <button
                    type="button"
                    onClick={() => setShowContactDetails(!showContactDetails)}
                    className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-purple-200 hover:bg-white/5 transition-colors focus:outline-none"
                  >
                    <span className="flex items-center gap-2">
                      <User className="h-4 w-4 text-purple-400" />
                      <span className="text-xs">Contact Details (Optional)</span>
                    </span>
                    {showContactDetails ? (
                      <ChevronUp className="h-4 w-4 text-purple-300" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-purple-300" />
                    )}
                  </button>

                  <AnimatePresence>
                    {showContactDetails && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: 'easeInOut' }}
                        className="border-t border-white/5 px-4 py-4"
                      >
                        <div className="grid gap-4 sm:grid-cols-2 text-xs">
                          {/* Name */}
                          <div className="grid gap-1.5">
                            <label htmlFor="name" className="font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                              <User className="h-3.5 w-3.5 text-purple-400" /> Name
                            </label>
                            <input
                              id="name"
                              name="name"
                              type="text"
                              value={formData.name}
                              onChange={handleInputChange}
                              placeholder="Your name"
                              disabled={isSubmitting}
                              className="h-10 w-full rounded-lg border border-white/10 bg-slate-950/40 px-3 py-2 text-white placeholder-slate-600 focus:border-purple-500/60 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all duration-200"
                            />
                          </div>

                          {/* Email */}
                          <div className="grid gap-1.5">
                            <label htmlFor="email" className="font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                              <Mail className="h-3.5 w-3.5 text-purple-400" /> Email
                            </label>
                            <input
                              id="email"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              placeholder="Your email address"
                              disabled={isSubmitting}
                              className="h-10 w-full rounded-lg border border-white/10 bg-slate-950/40 px-3 py-2 text-white placeholder-slate-600 focus:border-purple-500/60 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all duration-200"
                            />
                          </div>

                          {/* Company */}
                          <div className="grid gap-1.5">
                            <label htmlFor="company" className="font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                              <Building className="h-3.5 w-3.5 text-purple-400" /> Organization
                            </label>
                            <input
                              id="company"
                              name="company"
                              type="text"
                              value={formData.company}
                              onChange={handleInputChange}
                              placeholder="Practice or company name"
                              disabled={isSubmitting}
                              className="h-10 w-full rounded-lg border border-white/10 bg-slate-950/40 px-3 py-2 text-white placeholder-slate-600 focus:border-purple-500/60 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all duration-200"
                            />
                          </div>

                          {/* Role */}
                          <div className="grid gap-1.5">
                            <label htmlFor="role" className="font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                              <Briefcase className="h-3.5 w-3.5 text-purple-400" /> Professional Role
                            </label>
                            <input
                              id="role"
                              name="role"
                              type="text"
                              value={formData.role}
                              onChange={handleInputChange}
                              placeholder="e.g. Clinician, Admin"
                              disabled={isSubmitting}
                              className="h-10 w-full rounded-lg border border-white/10 bg-slate-950/40 px-3 py-2 text-white placeholder-slate-600 focus:border-purple-500/60 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all duration-200"
                            />
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Submit button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="group relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-3.5 text-sm font-bold text-white shadow-lg shadow-purple-500/10 hover:from-purple-500 hover:to-indigo-500 hover:shadow-purple-500/25 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-70 disabled:pointer-events-none md:w-auto"
              >
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.15),_transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                {isSubmitting ? (
                  <>
                    <Send className="h-4 w-4 animate-spin" /> Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /> Submit Review
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RatingsPage;
